import { z } from "zod"

import {
  COPY_FIELD_ROLES,
  marketplaceIdSchema,
  requirementLevelSchema,
  runFeedPreviewFreshnessSchema,
} from "@nova-ai/shared"

export const PRODUCT_OVERVIEW_ROUTE_PATTERN = "/products/:sku/overview"

export function productOverviewRoute(configSku: string): string {
  return `/api/products/${encodeURIComponent(configSku)}/overview`
}

export const PRODUCT_MARKETPLACE_STATE = {
  listed: "listed",
  nok: "nok",
  pending: "pending",
} as const
export type ProductMarketplaceState =
  (typeof PRODUCT_MARKETPLACE_STATE)[keyof typeof PRODUCT_MARKETPLACE_STATE]

const productLinkPreviewSchema = z.object({
  name: z.string().nullable(),
  imageUrl: z.string().url().nullable(),
  variantLabel: z.string().nullable(),
})
export type ProductLinkPreview = z.infer<typeof productLinkPreviewSchema>

const productMarketplaceLinkSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("unavailable") }),
  z.object({
    kind: z.literal("product"),
    url: z.string().url(),
    preview: productLinkPreviewSchema,
  }),
  z.object({
    kind: z.literal("variants"),
    variants: z
      .array(
        z.object({
          simpleSku: z.string().min(1),
          url: z.string().url(),
          preview: productLinkPreviewSchema,
        })
      )
      .min(1),
  }),
])
export type ProductMarketplaceLink = z.infer<typeof productMarketplaceLinkSchema>

export const marketplaceProductImportEvidenceSchema = z.object({
  eventId: z.number().int().positive(),
  marketplaceImportId: z.string().min(1),
  feedArtifactId: z.number().int().positive(),
  feedSha256: z.string().min(1),
  selectionKind: z.string().min(1),
  coverage: z.object({
    kind: z.enum(["marketplace", "master_category", "explicit_skus"]),
    key: z.string().nullable(),
    complete: z.boolean(),
  }),
  terminalAt: z.string().min(1),
  run: z.object({ id: z.number().int().positive(), slug: z.string().nullable() }).nullable(),
})
export type MarketplaceProductImportEvidence = z.infer<
  typeof marketplaceProductImportEvidenceSchema
>

export const marketplaceProductStateExplanationSchema = z.object({
  marketplace: marketplaceIdSchema,
  stale: z.boolean(),
  reason: z.string().nullable(),
  lastCovering: marketplaceProductImportEvidenceSchema.nullable(),
  staledBy: marketplaceProductImportEvidenceSchema.nullable(),
})
export type MarketplaceProductStateExplanation = z.infer<
  typeof marketplaceProductStateExplanationSchema
>

const productOverviewMarketplaceSchema = z.object({
  marketplace: marketplaceIdSchema,
  state: z.enum([
    PRODUCT_MARKETPLACE_STATE.listed,
    PRODUCT_MARKETPLACE_STATE.nok,
    PRODUCT_MARKETPLACE_STATE.pending,
  ]),
  isStale: z.boolean(),
  isSetAside: z.boolean(),
  isDelisted: z.boolean(),
  explanation: marketplaceProductStateExplanationSchema,
  link: productMarketplaceLinkSchema,
})

export const productOverviewSchema = z.object({
  configSku: z.string().min(1),
  name: z.string().nullable(),
  images: z.array(
    z.object({
      url: z.string().url(),
      role: z.enum(["main", "gallery", "variant"]),
    })
  ),
  marketplaces: z.array(productOverviewMarketplaceSchema),
})
export type ProductOverview = z.infer<typeof productOverviewSchema>

const productDetailValueSchema = z.object({
  attrCode: z.string().min(1),
  simpleSku: z.string().nullable(),
  value: z.string(),
})
export type ProductDetailValue = z.infer<typeof productDetailValueSchema>

const scopedProductValueSchema = z.object({
  config: productDetailValueSchema.nullable(),
  variants: z.array(z.object({ simpleSku: z.string().min(1), value: productDetailValueSchema })),
})

const productValueChecklistAttributeSchema = z.object({
  attrCode: z.string().min(1),
  label: z.string().min(1).nullable(),
  requirement: requirementLevelSchema.nullable(),
  values: scopedProductValueSchema,
})
export type ProductValueChecklistAttribute = z.infer<typeof productValueChecklistAttributeSchema>

const productValueCopywritingFieldSchema = productValueChecklistAttributeSchema.extend({
  roles: z.array(z.enum(COPY_FIELD_ROLES)).min(1),
  format: z.enum(["plain", "markdown", "html"]),
})
export type ProductValueCopywritingField = z.infer<typeof productValueCopywritingFieldSchema>

export const PRODUCT_VALUES_SCHEMA_STATE = {
  available: "available",
  missingCategory: "missing_category",
  unavailable: "unavailable",
} as const
export type ProductValuesSchemaState =
  (typeof PRODUCT_VALUES_SCHEMA_STATE)[keyof typeof PRODUCT_VALUES_SCHEMA_STATE]

export const PRODUCT_VALUES_INSPECTION_STATE = {
  available: "available",
  excluded: "excluded",
  unavailable: "unavailable",
} as const

const productValuesInspectionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal(PRODUCT_VALUES_INSPECTION_STATE.available),
    generationId: z.number().int().positive(),
    revisionId: z.number().int().positive(),
    freshness: runFeedPreviewFreshnessSchema,
  }),
  z.object({
    kind: z.literal(PRODUCT_VALUES_INSPECTION_STATE.excluded),
    generationId: z.number().int().positive(),
    reason: z.string(),
    freshness: runFeedPreviewFreshnessSchema,
  }),
  z.object({
    kind: z.literal(PRODUCT_VALUES_INSPECTION_STATE.unavailable),
    reason: z.string(),
  }),
])
export type ProductValuesInspection = z.infer<typeof productValuesInspectionSchema>

export const productValuesDetailSchema = z.object({
  inspection: productValuesInspectionSchema,
  configSku: z.string().min(1),
  marketplaces: z.array(marketplaceIdSchema),
  selectedMarketplace: marketplaceIdSchema.nullable(),
  category: z
    .object({
      code: z.string().min(1),
      label: z.string().min(1).nullable(),
    })
    .nullable(),
  schemaState: z.enum([
    PRODUCT_VALUES_SCHEMA_STATE.available,
    PRODUCT_VALUES_SCHEMA_STATE.missingCategory,
    PRODUCT_VALUES_SCHEMA_STATE.unavailable,
  ]),
  schemaMessage: z.string().nullable(),
  copywriting: z.array(productValueCopywritingFieldSchema),
  attributes: z.array(productValueChecklistAttributeSchema),
  offSchema: z.array(productValueChecklistAttributeSchema),
})
export type ProductValuesDetail = z.infer<typeof productValuesDetailSchema>
